import { VALIDATION_ENUM } from "enums";
import { Field, Formik, Form } from "formik";
import {
  useStoreCompany,
  useUpdateCompany,
  searchCompaniesFetcher,
} from "hooks/companies";
import { useGetCountriesCurrency } from "hooks/countries";
import { useGetIndustriesHook } from "hooks/industries";
import { useRef, useState } from "react";
import "./AddCompany.scss";
// import { useAddAdmin, useUpdateUser } from "hooks/users";
import {
  Button,
  CalendarInput,
  FullscreenModal,
  Input,
  LargeSwitch,
  SelectComponent,
  AsyncSelectComponent,
  Textarea,
  DropFileInput,
} from "shared";
import { CompanyStoreType, CompanyType } from "types/CompanyTypes";
import {
  companyStatusOpitons,
  getCurrencyFromUuid,
  scrollToError,
} from "utils";
import * as Yup from "yup";
import { isValidPolishNip } from "utils/validation/nipValidation";

interface AddCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  company?: CompanyType | null;
  defaultType?: "stock" | "share";
}

const AddCompanyModal: React.FC<AddCompanyModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  company,
  defaultType,
}) => {
  const { mutateAsync: storeCompany } = useStoreCompany();
  const { mutateAsync: updateCompany } = useUpdateCompany();
  const { data: industries } = useGetIndustriesHook();
  const { data: currencyOptions } = useGetCountriesCurrency();
  const formRef = useRef<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isIndustryDisabled, setIsIndustryDisabled] = useState(false);
  const [isTabDisabled, setIsTabDisabled] = useState(false);

  const initialValues = {
    name: company?.name || "",
    description: company?.description || "",
    industryUuid: company?.industryUuid || "",
    initialSharesCount: company?.initialSharesCount || null,
    currencyUuid: company?.currencyUuid || "",
    sharePrice: company?.sharesValue || null,
    pricePerShare: company?.pricePerShare || null,
    purchaseDate: null,
    publicationDate: company?.publicationDate || null,
    status: company?.status || null,
    type: company?.type || defaultType || "stock",
    nip: company?.nip || "",
    invoice: null,
  };

  const loadCompanyOptions = async (inputValue: string) => {
    const q = inputValue.trim();
    if (q.length < 2) return [];
    const items = await searchCompaniesFetcher(
      q,
      undefined,
      undefined,
      defaultType,
    );
    return items.map((c) => ({ label: c.name, value: c.name, company: c }));
  };

  const truncateTo2 = (n: number) => Math.floor(n * 100) / 100;

  const onSubmit = async (
    values: CompanyStoreType & { invoice?: File | null },
  ) => {
    setIsSubmitting(true);
    try {
      if (!!company) {
        const {
          currencyUuid,
          initialSharesCount,
          sharePrice,
          status,
          pricePerShare,
          invoice,
          ...rest
        } = values as any;
        await updateCompany(
          {
            ...rest,
            id: company.id,
            type: company.type === "stock" ? "stock" : rest.type,
            availableShares: undefined,
          },
          {
            onSuccess: () => {
              onClose();
            },
          },
        );
      } else {
        const raw =
          values.sharePrice && values.initialSharesCount
            ? values.sharePrice / values.initialSharesCount
            : 0;

        const summary =
          Number.isFinite(raw) && !Number.isNaN(raw) ? truncateTo2(raw) : 0;

        await storeCompany(
          {
            ...values,
            pricePerShare: summary,
          },
          {
            onSuccess: () => {
              onSuccess && onSuccess();
              onClose();
            },
          },
        );
      }
    } finally {
      scrollToError(formRef.current?.errors);
      setIsSubmitting(false);
    }
  };

  const validation = () =>
    Yup.object().shape({
      name: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .max(50, VALIDATION_ENUM.MAX_50)
        .min(3, VALIDATION_ENUM.MIN3)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE,
        ),
      description: Yup.string()
        .min(3, VALIDATION_ENUM.MIN3)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE,
        ),
      industryUuid: Yup.string().required(VALIDATION_ENUM.REQUIRED),
      initialSharesCount: Yup.number()
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === "" || originalValue == null) return null;
          const casted =
            typeof originalValue === "string" ? Number(originalValue) : value;
          return Number.isNaN(casted) ? null : casted;
        })
        .when([], {
          is: () => !company,
          then: (schema) =>
            schema.when(["type"], {
              is: (type: string) => type === "stock",
              then: (s) =>
                s
                  .required(VALIDATION_ENUM.REQUIRED)
                  .min(1, VALIDATION_ENUM.MIN_1)
                  .test(
                    "stock-integer",
                    "Ilość akcji musi być liczbą całkowitą",
                    (value) => value == null || Number.isInteger(value),
                  ),
              otherwise: (s) => s.required(VALIDATION_ENUM.REQUIRED),
            }),
          otherwise: (schema) => schema.notRequired(),
        }),

      currencyUuid: Yup.string().required(VALIDATION_ENUM.REQUIRED),

      sharePrice: Yup.number().when([], {
        is: () => !company,
        then: (schema) => schema.required(VALIDATION_ENUM.REQUIRED),
        otherwise: (schema) => schema.notRequired(),
      }),

      purchaseDate: Yup.string().when([], {
        is: () => !company,
        then: (schema) => schema.required(VALIDATION_ENUM.REQUIRED),
        otherwise: (schema) => schema.notRequired(),
      }),
      publicationDate: Yup.string().nullable(),
      status: Yup.string().required(VALIDATION_ENUM.REQUIRED),
      type: Yup.string().required(VALIDATION_ENUM.REQUIRED),
      nip: Yup.string()
        .max(11)
        .matches(/^\d*$/, "NIP może zawierać tylko cyfry")
        .test("nip-control", "Niepoprawny numer NIP", (value) => {
          const v = value?.trim();
          if (!v) return true;
          return isValidPolishNip(v);
        }),
    });

  return (
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <h2 className="fullscreen-modal__header">
        {!!company
          ? "Edycja spółki"
          : defaultType === "stock"
          ? "Dodaj spółkę akcyjną dodaną poza Reliance"
          : defaultType === "share"
          ? "Dodaj spółkę udziałową dodaną poza Reliance"
          : "Dodaj spółkę"}
      </h2>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values: any) => await onSubmit(values)}
        validationSchema={validation()}
        enableReinitialize
        innerRef={formRef}
        validateOnMount={false}
      >
        {({ errors, values, setFieldValue, submitForm }) => {
          const summaryRaw =
            !!values?.sharePrice &&
            !!values?.initialSharesCount &&
            values?.sharePrice > 0 &&
            values?.initialSharesCount > 0
              ? values?.sharePrice / values?.initialSharesCount
              : 0;

          const summary =
            Number.isFinite(summaryRaw) && !Number.isNaN(summaryRaw)
              ? truncateTo2(summaryRaw)
              : 0;

          const isEditingStockCompany = !!company;
          const effectiveType = isEditingStockCompany ? "stock" : values?.type;
          const isStock = effectiveType === "stock";

          return (
            <Form className="m-width-100 fullscreen-modal__select-margin">
              {!isEditingStockCompany && !defaultType && (
                <LargeSwitch
                  disabled={!!defaultType && isTabDisabled}
                  state={values.type}
                  className="add-company__switch"
                  options={[
                    { label: "Akcje", value: "stock" },
                    { label: "Udziały", value: "share" },
                  ]}
                  onChange={(value: string) => setFieldValue("type", value)}
                />
              )}
              {!!company ? (
                <Field
                  type="text"
                  id="name"
                  name="name"
                  label={"Nazwa spółki"}
                  as={Input}
                  maxLength={50}
                />
              ) : (
                <Field
                  id="name"
                  name="name"
                  className="select--no-arrow"
                  label={"Nazwa spółki"}
                  as={AsyncSelectComponent as any}
                  loadOptions={loadCompanyOptions}
                  minCharsToSearch={3}
                  onSelectOption={(opt: any) => {
                    const c = opt?.company as any;
                    if (c && c.type) {
                      setFieldValue(
                        "type",
                        c.type === "stock" ? "stock" : "share",
                      );
                      setIsTabDisabled(true);
                    }
                    if (c && c.industryUuid) {
                      setFieldValue("industryUuid", c.industryUuid);
                      setIsIndustryDisabled(true);
                    } else {
                      setIsIndustryDisabled(false);
                      setIsTabDisabled(false);
                    }
                  }}
                  onInputTyping={() => {
                    setIsIndustryDisabled(false);
                    setIsTabDisabled(false);
                  }}
                />
              )}

              <Field
                type="textarea"
                id="description"
                optional
                name="description"
                label={"Opis"}
                component={Textarea}
                maxLength={1000}
                errors={errors}
              />

              <Field
                label="Branża"
                name="industryUuid"
                id="industryUuid"
                as={SelectComponent}
                options={industries}
                disabled={isIndustryDisabled}
              />

              <Field
                type="date"
                id="publicationDate"
                name="publicationDate"
                label={"Data publikacji na giełdzie"}
                optional
                component={CalendarInput}
              />

              {!company && (
                <>
                  <Field
                    label="Waluta"
                    name="currencyUuid"
                    id="currencyUuid"
                    as={SelectComponent}
                    options={currencyOptions}
                  />

                  <Field
                    type="date"
                    id="purchaseDate"
                    errors={errors}
                    name="purchaseDate"
                    label={`Data zakupu ${
                      effectiveType === "stock" ? "akcji" : "udziałów"
                    }`}
                    component={CalendarInput}
                  />

                  <Field
                    type="number"
                    id="initialSharesCount"
                    name="initialSharesCount"
                    label={`Ilość ${
                      effectiveType === "stock" ? "akcji" : "udziałów"
                    }`}
                    as={Input}
                    maxLength={10}
                    precision={isStock ? 0 : 2}
                    inputMode={isStock ? "numeric" : "decimal"}
                    step={isStock ? 1 : "any"}
                  />
                  <div className="input--currency">
                    <Field
                      type="number"
                      maxLength={10}
                      id="sharePrice"
                      name="sharePrice"
                      label={`Suma wartości ${
                        effectiveType === "stock" ? "akcji" : "udziałów"
                      }`}
                      as={Input}
                    />
                    <span>{getCurrencyFromUuid(values.currencyUuid)}</span>
                  </div>
                  <div className={`add-share__summary`}>
                    <label className="input__label">
                      Wartość {isStock ? "jednej akcji" : "jednego udziału"}
                    </label>
                    <input
                      type="text"
                      className="input"
                      readOnly
                      value={summary || 0}
                    />
                    <span>
                      {values.currencyUuid &&
                        getCurrencyFromUuid(values.currencyUuid)}
                    </span>
                  </div>
                  <Field
                    label="Umowa"
                    name="invoice"
                    optional
                    id="invoice"
                    as={DropFileInput}
                    accept=".pdf"
                  />
                  <Field
                    label="Status"
                    name="status"
                    id="status"
                    as={SelectComponent}
                    options={companyStatusOpitons}
                  />
                </>
              )}
              <Field
                type="number"
                id="nip"
                name="nip"
                optional
                label={"NIP"}
                as={Input}
                maxLength={11}
              />
              <div className="fullscreen-modal__buttons">
                <Button
                  label="Anuluj"
                  onClick={onClose}
                  className="button--stroke button--rounded"
                />
                <Button
                  label={"Potwierdź"}
                  type="button"
                  onClick={async () => {
                    await submitForm().then(() => {
                      if (
                        Object.keys(formRef.current?.errors || {}).length > 0
                      ) {
                        scrollToError(formRef.current?.errors);
                      }
                    });
                  }}
                  className=" button--rounded button--black"
                  isLoading={isSubmitting}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </FullscreenModal>
  );
};

export default AddCompanyModal;
